import React from "react";
import { useInvestigationState } from "../../investigation/useInvestigationState";
import { Link } from "react-router-dom";
import { FileText, Network } from "lucide-react";
import { DossierLabel } from "../dossier/DossierLabel";
import { useTopicEntities, allEntities } from "../../hooks/use-topic-entities";
import { pathForEntity, KIND_LABEL } from "./EntityConnections";

/**
 * INVESTIGATION NETWORK — every connected entity of this dossier as a
 * doorway tile (portrait/seal, name, type, dossier count). "Explore Full
 * Network" expands the complete node list in place.
 */
const Tile = ({ e }) => (
  <Link
    to={pathForEntity(e)}
    className="group flex items-center gap-3 border p-3 hover:border-[#D92D20] transition-colors"
    style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}
    data-testid={`network-node-${e.slug}`}
  >
    <span className="w-11 h-14 shrink-0 flex items-center justify-center overflow-hidden"
      style={{ border: "1px solid #221F1C", background: "#0B0A0D" }}>
      {e.portrait_url ? (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${e.portrait_url}`}
          alt={e.name}
          className="max-w-full max-h-full object-contain"
          style={{ filter: "sepia(0.15)" }}
          loading="lazy"
        />
      ) : (
        <FileText size={14} className="text-[#454038]" />
      )}
    </span>
    <span className="min-w-0">
      <span className="block text-[13px] text-[#E4DFD3] font-heading leading-tight truncate group-hover:text-[#D92D20] transition-colors">
        {e.name}
      </span>
      <span className="block font-mono text-[8.5px] uppercase tracking-[0.18em] text-[#A68F5E] mt-0.5">
        {KIND_LABEL[e.kind] || "File"}
      </span>
      <span className="block font-mono text-[8.5px] uppercase tracking-[0.15em] text-[#5A554C] mt-0.5">
        {e.cameo_count} dossier{e.cameo_count === 1 ? "" : "s"}
      </span>
    </span>
  </Link>
);

export const InvestigationNetwork = ({ topic }) => {
  const groups = useTopicEntities(topic.slug);
  const [expanded, setExpanded] = useInvestigationState("inv-network-expanded", false);
  const all = allEntities(groups);
  if (all.length === 0) return null;
  const shown = expanded ? all : all.slice(0, 8);

  return (
    <section className="mt-16" data-testid="investigation-network">
      <DossierLabel as="h2" className="mt-0">Investigation Network</DossierLabel>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mt-2 mb-5 flex items-center gap-2">
        <Network size={11} className="text-[#D92D20]" />
        {all.length} connected file{all.length === 1 ? "" : "s"} — every node is a doorway
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {shown.map((e) => <Tile key={e.slug} e={e} />)}
      </div>
      {all.length > 8 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A68F5E] hover:text-[#D92D20] transition-colors border px-4 py-2"
          style={{ borderColor: "#3A3630" }}
          data-testid="explore-full-network"
        >
          {expanded ? "Collapse Network" : `Explore Full Network (${all.length}) →`}
        </button>
      )}
    </section>
  );
};

export default InvestigationNetwork;
